/* ============================================
   NODUS 08 · VOICES
   Scroll-snap carousel with pagination dots and optional autoplay.
   ============================================ */

if (!customElements.get('nodus-voices-carousel')) {
	class NodusVoicesCarousel extends HTMLElement {
		connectedCallback() {
			this.track = this.querySelector('[data-nodus-voices-track]');
			this.dotsHolder = this.querySelector('[data-nodus-voices-dots]');
			this.prevButton = this.querySelector('[data-nodus-voices-prev]');
			this.nextButton = this.querySelector('[data-nodus-voices-next]');

			if (!this.track) return;

			this.page = 0;
			this.timer = null;
			this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

			this.handlePrev = () => this.goTo(this.page - 1, true);
			this.handleNext = () => this.goTo(this.page + 1, true);
			this.handleScroll = this.onScroll.bind(this);
			this.handleResize = this.onResize.bind(this);
			this.handlePause = () => this.stopAutoplay();
			this.handleResume = () => this.startAutoplay();
			this.handleDotClick = this.onDotClick.bind(this);

			if (this.prevButton) this.prevButton.addEventListener('click', this.handlePrev);

			if (this.nextButton) this.nextButton.addEventListener('click', this.handleNext);

			if (this.dotsHolder) this.dotsHolder.addEventListener('click', this.handleDotClick);

			this.track.addEventListener('scroll', this.handleScroll, { passive: true });
			window.addEventListener('resize', this.handleResize);
			this.addEventListener('mouseenter', this.handlePause);
			this.addEventListener('mouseleave', this.handleResume);
			this.addEventListener('focusin', this.handlePause);
			this.addEventListener('focusout', this.handleResume);

			this.buildDots();
			this.startAutoplay();
		}

		disconnectedCallback() {
			this.stopAutoplay();

			if (this.prevButton && this.handlePrev) this.prevButton.removeEventListener('click', this.handlePrev);

			if (this.nextButton && this.handleNext) this.nextButton.removeEventListener('click', this.handleNext);

			if (this.dotsHolder && this.handleDotClick) {
				this.dotsHolder.removeEventListener('click', this.handleDotClick);
			}

			if (this.track && this.handleScroll) this.track.removeEventListener('scroll', this.handleScroll);

			if (this.handleResize) window.removeEventListener('resize', this.handleResize);

			this.removeEventListener('mouseenter', this.handlePause);
			this.removeEventListener('mouseleave', this.handleResume);
			this.removeEventListener('focusin', this.handlePause);
			this.removeEventListener('focusout', this.handleResume);
		}

		get pageCount() {
			const width = this.track.clientWidth;

			if (width === 0) return 1;

			return Math.max(1, Math.round(this.track.scrollWidth / width));
		}

		buildDots() {
			if (!this.dotsHolder) return;

			const total = this.pageCount;

			this.dotsHolder.textContent = '';

			if (total < 2) {
				this.dotsHolder.hidden = true;
				return;
			}

			this.dotsHolder.hidden = false;

			for (let index = 0; index < total; index += 1) {
				const dot = document.createElement('button');

				dot.type = 'button';
				dot.className = 'nodus-voices__dot';
				dot.dataset.page = String(index);
				dot.setAttribute('aria-label', `${index + 1} / ${total}`);
				this.dotsHolder.appendChild(dot);
			}

			this.syncDots();
		}

		syncDots() {
			if (!this.dotsHolder) return;

			for (const dot of this.dotsHolder.children) {
				const isActive = Number(dot.dataset.page) === this.page;

				dot.classList.toggle('is-active', isActive);
				dot.setAttribute('aria-current', isActive ? 'true' : 'false');
			}
		}

		goTo(page, restart) {
			const total = this.pageCount;

			this.page = ((page % total) + total) % total;
			this.track.scrollTo({
				left: this.page * this.track.clientWidth,
				behavior: this.reducedMotion.matches ? 'auto' : 'smooth',
			});
			this.syncDots();

			if (restart) this.startAutoplay();
		}

		onDotClick(event) {
			const dot = event.target.closest('[data-page]');

			if (dot) this.goTo(Number(dot.dataset.page), true);
		}

		onScroll() {
			const width = this.track.clientWidth;

			if (width === 0) return;

			const page = Math.round(this.track.scrollLeft / width);

			if (page !== this.page) {
				this.page = page;
				this.syncDots();
			}
		}

		onResize() {
			this.buildDots();
			this.goTo(0, false);
		}

		startAutoplay() {
			this.stopAutoplay();

			if (this.dataset.autoplay !== 'true' || this.reducedMotion.matches) return;

			if (this.pageCount < 2) return;

			const delay = (Number(this.dataset.autoplayDelay) || 6) * 1000;

			this.timer = window.setInterval(() => this.goTo(this.page + 1, false), delay);
		}

		stopAutoplay() {
			if (this.timer) {
				window.clearInterval(this.timer);
				this.timer = null;
			}
		}
	}

	customElements.define('nodus-voices-carousel', NodusVoicesCarousel);
}
